import { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '../dialog';
import Loading from '../loading';
import LegalDocumentViewer from '../legal-document-viewer';
import { getTask, postTask } from '../../../services';
import { Urls } from '../../../config/urls';
import legalDocumentsUpdateIcon from '../../../assets/images/legal-documents-update.svg?url';
import './styles.scss';

interface LegalDocumentAcceptanceStatusItem {
  versionId: number;
  documentType: 'TERMS_AND_CONDITIONS' | 'PRIVACY_POLICY';
  versionCode: string;
  country?: string | null;
  language: string;
  contentUrl: string;
  acceptedCurrentVersion: boolean;
}

interface LegalDocumentAcceptanceStatusResponse {
  pendingAcceptance: boolean;
  documents: LegalDocumentAcceptanceStatusItem[];
}

interface LegalAcceptanceModalProps {
  token?: string;
  language?: string;
}

const COPY = {
  es: {
    title: 'Actualizamos nuestros Términos y condiciones y Política de privacidad',
    body: 'Para continuar, revisa y acepta la versión vigente de ambos documentos.',
    checkbox: 'He leído y acepto la versión vigente de los Términos y condiciones y la Política de privacidad.',
    action: 'Aceptar y continuar',
    tabs: {
      TERMS_AND_CONDITIONS: 'Términos y condiciones',
      PRIVACY_POLICY: 'Política de privacidad',
    },
  },
  en: {
    title: 'We updated our Terms and conditions and Privacy policy',
    body: 'To continue, review and accept the current version of both documents.',
    checkbox: 'I have read and accept the current version of the Terms and conditions and Privacy policy.',
    action: 'Accept and continue',
    tabs: {
      TERMS_AND_CONDITIONS: 'Terms and conditions',
      PRIVACY_POLICY: 'Privacy policy',
    },
  },
  pt: {
    title: 'Atualizamos nossos Termos e condições e Política de privacidade',
    body: 'Para continuar, revise e aceite a versão vigente de ambos os documentos.',
    checkbox: 'Li e aceito a versão vigente dos Termos e condições e da Política de privacidade.',
    action: 'Aceitar e continuar',
    tabs: {
      TERMS_AND_CONDITIONS: 'Termos e condições',
      PRIVACY_POLICY: 'Política de privacidade',
    },
  },
};

const LegalAcceptanceModal = ({ token, language = 'en' }: LegalAcceptanceModalProps) => {
  const normalizedLanguage = language.toLowerCase().startsWith('pt')
    ? 'pt'
    : language.toLowerCase().startsWith('es')
      ? 'es'
      : 'en';
  const copy = COPY[normalizedLanguage];
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [status, setStatus] = useState<LegalDocumentAcceptanceStatusResponse | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<'TERMS_AND_CONDITIONS' | 'PRIVACY_POLICY'>(
    'TERMS_AND_CONDITIONS'
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      setLoading(true);
      const { response } = await getTask({
        url: Urls.api.legalDocumentAcceptanceStatus,
        token,
      });

      if (!cancelled) {
        setStatus(response || { pendingAcceptance: false, documents: [] });
        const firstPending = response?.documents?.find(
          (item: LegalDocumentAcceptanceStatusItem) => !item.acceptedCurrentVersion
        );
        setSelectedDocumentType(firstPending?.documentType || 'TERMS_AND_CONDITIONS');
        setLoading(false);
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const documents = status?.documents || [];
  const pendingAcceptance = Boolean(status?.pendingAcceptance && documents.length > 0);
  const selectedDocument = useMemo(
    () => documents.find(document => document.documentType === selectedDocumentType) || documents[0],
    [documents, selectedDocumentType]
  );

  const handleAccept = async () => {
    if (!token || !status?.documents?.length) {
      return;
    }

    setSubmitting(true);
    const { response } = await postTask({
      url: Urls.api.acceptLegalDocuments,
      token,
      body: {
        versionIds: status.documents.map(document => document.versionId),
        language: normalizedLanguage,
      },
    });

    if (response) {
      setStatus({
        pendingAcceptance: false,
        documents: status.documents.map(document => ({ ...document, acceptedCurrentVersion: true })),
      });
      setAccepted(false);
    }
    setSubmitting(false);
  };

  if (loading || !pendingAcceptance) {
    return null;
  }

  return (
    <Dialog
      open
      type="md"
      dividers={false}
      unpaddingContent
      withoutTitle
      fullScreenMobile
      paperClassName="legal-acceptance-dialog-paper"
      body={
        <div className="legal-acceptance-modal">
          <div className="legal-acceptance-modal__header">
            <img
              className="legal-acceptance-modal__icon"
              src={legalDocumentsUpdateIcon}
              alt=""
              width={60}
              height={60}
            />
            <h2 className="legal-acceptance-modal__title">{copy.title}</h2>
            <p className="legal-acceptance-modal__body">{copy.body}</p>
            <div className="legal-acceptance-modal__tabs">
              {documents.map(document => (
                <Button
                  key={document.documentType}
                  className="legal-acceptance-modal__tab"
                  variant={document.documentType === selectedDocumentType ? 'contained' : 'outlined'}
                  onClick={() => setSelectedDocumentType(document.documentType)}
                >
                  {copy.tabs[document.documentType]}
                </Button>
              ))}
            </div>
          </div>
          <div className="legal-acceptance-modal__viewer">
            {selectedDocument ? (
              <LegalDocumentViewer
                documentType={selectedDocument.documentType}
                language={selectedDocument.language}
                contentUrl={selectedDocument.contentUrl}
              />
            ) : (
              <Loading />
            )}
          </div>
          <div className="legal-acceptance-modal__footer">
            <FormControlLabel
              className="legal-acceptance-modal__checkbox"
              control={<Checkbox checked={accepted} onChange={event => setAccepted(event.target.checked)} />}
              label={copy.checkbox}
            />
            <Button
              className="legal-acceptance-modal__action"
              variant="contained"
              onClick={handleAccept}
              disabled={!accepted || submitting}
            >
              {copy.action}
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default LegalAcceptanceModal;
